import { Select } from '../../Select';
import { ObjectWithId } from '../types';
import { useMemo, useState, useEffect } from 'react';
import { GenericTable } from '../GenericTable';
import 'dayjs/locale/pt-br'; // load on demand

export type CategoryTableProps<T extends ObjectWithId> = {
  rows: T[];
  getSummary: (row: T) => string;
  newCategory: (category: string) => void;
  selectLabel?: string;
  isSelectable?: boolean;
  handleSelect?: (selected: T) => void;
  isFetching?: boolean;
  aboveHeader?: (FilterInput: JSX.Element) => JSX.Element;
  customSort?: Partial<Record<keyof T, (a: T, b: T) => number>>;
  getCategory: (row: T) => string;
  selectableAccordionTitle?: string;
};

export function CategoryTable<T extends ObjectWithId>({
  rows,
  getSummary,
  selectLabel = 'Filtrar por',
  newCategory,
  handleSelect,
  isFetching,
  isSelectable,
  aboveHeader,
  customSort,
  getCategory,
  selectableAccordionTitle,
}: CategoryTableProps<T>) {
  const [filterBy, setFilterBy] = useState<string>('');

  const options = useMemo(() => {
    const categoryValues = rows.map(getCategory);
    const uniqueCategoryValues = new Set(categoryValues);

    const opts = Array.from(uniqueCategoryValues).map((value) => ({
      value,
      label: value,
    }));

    return [{ value: 'Todos', label: 'Todos' }, ...opts];
  }, [rows]);

  const filteredRows = useMemo(() => {
    if (!filterBy) return rows;

    return rows.filter(
      (row) => getCategory(row) === filterBy || filterBy === 'Todos'
    );
  }, [filterBy, rows, getCategory]);

  useEffect(() => {
    newCategory(filterBy);
  }, [filterBy, newCategory]);

  return (
    <div className="h-full w-full flex">
      <div className="flex w-full">
        <GenericTable
          customSort={customSort}
          isSortable
          isFetching={isFetching}
          rows={filteredRows}
          getSummary={getSummary}
          Footer={<div></div>}
          isSelectable={isSelectable}
          onSelectRow={handleSelect}
          selectableAccordionTitle={selectableAccordionTitle}
          AboveHeader={
            aboveHeader ? (
              aboveHeader(
                <div className="w-[200px]">
                  <Select
                    label={selectLabel}
                    id={'filter-table'}
                    value={filterBy}
                    onChange={setFilterBy}
                    size={'small'}
                    options={options}
                    className="w-full text-start"
                  />
                </div>
              )
            ) : (
              <div className="flex justify-end w-full">
                <div className="w-full max-w-[200px]">
                  <Select
                    label={selectLabel}
                    id={'filter-table'}
                    value={filterBy}
                    onChange={setFilterBy}
                    size={'small'}
                    options={options}
                    className="w-full text-start"
                  />
                </div>
              </div>
            )
          }
        />
      </div>
    </div>
  );
}
